import moment from 'moment';
import {
  RefObject, useEffect, useRef, useState,
} from 'react';
import styled from 'styled-components';
import { Input, TextFieldInput } from '../../inputs';
import { ModelInputProps } from '../Input';
import { FormInput, useModelForm } from '../ModelForm';
import { Flex } from '../../../Flex';
import { useIsAutoSave } from '../AutoSave';
import { useFormConfigurationContext } from '../../../../..';

const Container = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  flex: 1;
`}`;

type ModelDateDMYProps = ModelInputProps & {
  showDay?: boolean,
  clearOnDisabled?: boolean,
}

export function ModelDateDMY(props: ModelDateDMYProps) {
  const {
    id = '', field, valueOverride, errorOverride, showDay = false, disabled,
    clearOnDisabled = false,
  } = props;

  const {
    errors,
    value,
    update,
    warnings,
  } = useModelForm(field, valueOverride, errorOverride);

  const { isDisabled } = useFormConfigurationContext(disabled);
  const { isAutoSave } = useIsAutoSave();

  const changed = useRef(false);

  const [day, setDay] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');

  const inputMonthRef = useRef<HTMLInputElement>(null);
  const inputYearRef = useRef<HTMLInputElement>(null);

  const [dayTouched, setDayTouched] = useState<boolean>(false);
  const [monthTouched, setMonthTouched] = useState<boolean>(false);
  const [yearTouched, setYearTouched] = useState<boolean>(false);

  const setAllTouched = () => {
    setDayTouched(true);
    setMonthTouched(true);
    setYearTouched(true);
  };

  const syncFromValue = () => {
    const m = moment(value);
    if (m.isValid()) {
      setDay(showDay ? m.format('DD') : '');
      setMonth(m.format('MM'));
      setYear(m.format('YYYY'));
      setAllTouched();
    } else {
      setDay('');
      setMonth('');
      setYear('');
    }
  };

  useEffect(() => {
    if (!changed.current) {
      syncFromValue();
    }
  }, [value]);

  useEffect(() => {
    if (isDisabled && clearOnDisabled) {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [isDisabled]);

  const reduceValue = (v: string, max: number): string => (
    Number(v) <= max
      ? v
      : reduceValue(v.substring(0, v.length - 1), max)
  );

  const maskValue = (v: string, maxLength: number) => v.replace(/[^0-9]/gmi, '').substring(0, maxLength);
  const maxedValue = (v: string, max: number) => (v.substring(0, 2) === '00' ? '0' : reduceValue(v, max));
  const padValue = (v: string, maxLength: number) => (Number(v) ? v.padStart(maxLength, '0') : v);
  const onlyNumbers = (v: string) => /^\d+$/.test(v) || v === '';

  const newIsoString = (yearV: string, monthV: string, dayV: string) => (
    yearV === '0000'
      ? null
      : `${yearV}-${padValue(monthV, 2)}-${showDay ? padValue(dayV, 2) : '01'}T00:00:00.000Z`
  );

  const updateDate = (yearV: string, monthV: string, dayV: string, disableIncrementUpdateCount: boolean = false) => {
    const isoString = newIsoString(yearV, monthV, dayV);
    const newDate = isoString ? moment(isoString).toISOString() : null;
    update({ [field]: newDate }, disableIncrementUpdateCount);
  };

  const focusAndSelect = (ref: RefObject<HTMLInputElement>) => {
    const { current } = ref;
    current?.focus();
    current?.select();
  };

  const onChangeDay = (newValue: string) => {
    changed.current = true;
    const maxValue = maxedValue(newValue, 31);
    const maskedValue = maskValue(maxValue, 2);

    if (maskedValue.length === 2) {
      focusAndSelect(inputMonthRef);
    }

    setDay(maskedValue);

    if (onlyNumbers(newValue)) {
      updateDate(year, month, maskedValue, isAutoSave);
    }
  };

  const onChangeMonth = (newValue: string) => {
    changed.current = true;
    const maxValue = maxedValue(newValue, 12);
    const maskedValue = maskValue(maxValue, 2);

    if (maskedValue.length === 2) {
      focusAndSelect(inputYearRef);
    }

    setMonth(maskedValue);

    if (onlyNumbers(newValue)) {
      updateDate(year, maskedValue, day, isAutoSave);
    }
  };

  const onChangeYear = (newValue: string) => {
    changed.current = true;
    const maskedValue = maskValue(newValue, 4);
    setYear(maskedValue);

    if (onlyNumbers(newValue)) {
      updateDate(maskedValue, month, day, isAutoSave);
    }
  };

  const handleDayOnBlur = () => {
    setDay(padValue(day, 2));
    setDayTouched(true);
    if (changed.current) {
      updateDate(year, month, day);
    }
    changed.current = false;
  };

  const handleMonthOnBlur = () => {
    setMonth(padValue(month, 2));
    setMonthTouched(true);
    if (changed.current) {
      updateDate(year, month, day);
    }
    changed.current = false;
  };

  const handleYearOnBlur = () => {
    setYearTouched(true);
    if (changed.current) {
      updateDate(year, month, day);
    }
    changed.current = false;
  };

  const allTouched = (dayTouched || !showDay) && monthTouched && yearTouched;
  const hasError = !!errors?.length && allTouched;

  return (
    <FormInput>
      <Input id={id} hasError={hasError} errors={errors} warnings={warnings} {...props}>
        <Container>
          {showDay && (
            <TextFieldInput
              id={`${id}-day`}
              hasError={hasError}
              value={day}
              onChange={(e) => onChangeDay(e.target.value)}
              placeholder="DD"
              disabled={isDisabled}
              onBlur={handleDayOnBlur}
            />
          )}
          <TextFieldInput
            id={`${id}-month`}
            hasError={hasError}
            value={month}
            onChange={(e) => onChangeMonth(e.target.value)}
            placeholder="MM"
            disabled={isDisabled}
            onBlur={handleMonthOnBlur}
            ref={inputMonthRef}
          />
          <TextFieldInput
            id={`${id}-year`}
            hasError={hasError}
            value={year}
            onChange={(e) => onChangeYear(e.target.value)}
            placeholder="YYYY"
            disabled={isDisabled}
            onBlur={handleYearOnBlur}
            ref={inputYearRef}
          />
        </Container>
      </Input>
    </FormInput>
  );
}
